import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Briefcase, MapPin, DollarSign, Calendar, Users, FileText, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const PostProjectPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    projectTitle: '',
    company: '',
    selectedDepartments: [],
    roles: [],
    locations: [],
    projectType: '',
    durations: [],
    startDate: '',
    expiryDate: '',
    expiryPreset: 'custom',
    description: '',
    requirements: '',
    budgetMin: '',
    budgetMax: '',
    showBudget: false,
    contactEmail: '',
    contactPhone: ''
  });

  // Temp inputs for adding multiple items
  const [currentRole, setCurrentRole] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [currentDuration, setCurrentDuration] = useState('');

  const departments = [
    'Art Department',
    'Camera Department',
    'Sound Department',
    'Makeup & Hair',
    'SFX / VFX',
    'Production',
    'Assistant Directors',
    'Lighting Department',
    'Post Production',
    'Script Department',
    'Direction',
    'Editing'
  ];

  // Check authentication on mount
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please login to post a project');
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one department is selected
    if (formData.selectedDepartments.length === 0) {
      toast.error('Please select at least one department');
      return;
    }

    // Validate at least one role is added
    if (formData.roles.length === 0) {
      toast.error('Please add at least one role/position');
      return;
    }

    // Validate at least one location is added
    if (formData.locations.length === 0) {
      toast.error('Please add at least one location');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare project data for API
      const projectData = {
        title: formData.projectTitle,
        description: formData.description,
        department: formData.selectedDepartments[0], // Use first selected department
        roles: formData.roles,
        locations: formData.locations.map(loc => {
          const parts = loc.split(',').map(p => p.trim());
          return { city: parts[0] || loc, state: parts[1] || '' };
        }),
        timeline: formData.startDate || null,
        budget: formData.showBudget && formData.budgetMin && formData.budgetMax 
          ? `₹${formData.budgetMin} - ₹${formData.budgetMax}` 
          : null,
        requirements: formData.requirements.split('\n').map(r => r.trim()).filter(r => r),
        contact_email: formData.contactEmail || null,
        contact_phone: formData.contactPhone || null,
        expires_at: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null
      };

      const response = await fetch(`${backendUrl}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send session cookie for auth
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Please login to post a project');
          navigate('/auth');
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create project');
      }

      const createdProject = await response.json();
      
      toast.success('✅ Hiring post created successfully!');
      setTimeout(() => {
        navigate('/discover/jobs');
      }, 2000);
      
    } catch (error) {
      console.error('Project creation error:', error);
      toast.error(error.message || 'Failed to create project. Please try again.');
    }
  };

  // Handle department checkbox toggle
  const toggleDepartment = (dept) => {
    if (formData.selectedDepartments.includes(dept)) {
      setFormData({
        ...formData,
        selectedDepartments: formData.selectedDepartments.filter(d => d !== dept)
      });
    } else {
      setFormData({
        ...formData,
        selectedDepartments: [...formData.selectedDepartments, dept]
      });
    }
  };

  // Add role
  const addRole = () => {
    if (currentRole.trim() && !formData.roles.includes(currentRole.trim())) {
      setFormData({
        ...formData,
        roles: [...formData.roles, currentRole.trim()]
      });
      setCurrentRole('');
    }
  };

  // Remove role
  const removeRole = (role) => {
    setFormData({
      ...formData,
      roles: formData.roles.filter(r => r !== role)
    });
  };

  // Add location
  const addLocation = () => {
    if (currentLocation.trim() && !formData.locations.includes(currentLocation.trim())) {
      setFormData({
        ...formData,
        locations: [...formData.locations, currentLocation.trim()]
      });
      setCurrentLocation('');
    }
  };

  // Remove location
  const removeLocation = (location) => {
    setFormData({
      ...formData,
      locations: formData.locations.filter(l => l !== location)
    });
  };

  // Add duration/timeline
  const addDuration = () => {
    if (currentDuration.trim() && !formData.durations.includes(currentDuration.trim())) {
      setFormData({
        ...formData,
        durations: [...formData.durations, currentDuration.trim()]
      });
      setCurrentDuration('');
    }
  };

  // Remove duration
  const removeDuration = (duration) => {
    setFormData({
      ...formData,
      durations: formData.durations.filter(d => d !== duration)
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Form Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <Card className="bg-card border-border shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-center">
                  Create a Hiring Post
                </CardTitle>
                <p className="text-center text-muted-foreground mt-2">
                  Find the perfect professionals for your film project
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Project Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-accent" />
                      Project Details
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium mb-2 block">Project Title *</label>
                        <Input
                          required
                          value={formData.projectTitle}
                          onChange={(e) => setFormData({ ...formData, projectTitle: e.target.value })}
                          placeholder="e.g., Period Drama Feature Film"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Company/Production House *</label>
                        <Input
                          required
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="e.g., Rainbow Productions"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Project Type *</label>
                        <Select value={formData.projectType} onValueChange={(val) => setFormData({ ...formData, projectType: val })}>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="feature-film">Feature Film</SelectItem>
                            <SelectItem value="web-series">Web Series</SelectItem>
                            <SelectItem value="commercial">Commercial</SelectItem>
                            <SelectItem value="music-video">Music Video</SelectItem>
                            <SelectItem value="documentary">Documentary</SelectItem>
                            <SelectItem value="short-film">Short Film</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Hiring Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Users className="w-5 h-5 text-accent" />
                      Hiring Requirements
                    </h3>
                    
                    {/* Multiple Department Selection */}
                    <div>
                      <label className="text-sm font-medium mb-3 block">Select Department(s) *</label>
                      <div className="grid md:grid-cols-3 gap-3 p-4 bg-muted/30 rounded-lg border border-border">
                        {departments.map(dept => (
                          <div key={dept} className="flex items-center space-x-2">
                            <Checkbox 
                              id={dept}
                              checked={formData.selectedDepartments.includes(dept)}
                              onCheckedChange={() => toggleDepartment(dept)}
                            />
                            <label htmlFor={dept} className="text-sm font-medium cursor-pointer">
                              {dept}
                            </label>
                          </div>
                        ))}
                      </div>
                      {formData.selectedDepartments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.selectedDepartments.map(dept => (
                            <Badge key={dept} className="bg-accent/10 text-accent border-accent/30">
                              {dept}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Multiple Roles/Positions */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Roles/Positions *</label>
                      <div className="flex gap-2">
                        <Input
                          value={currentRole}
                          onChange={(e) => setCurrentRole(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRole())}
                          placeholder="e.g., Art Director, Set Designer"
                        />
                        <Button type="button" onClick={addRole} className="bg-accent hover:bg-accent/90">
                          Add
                        </Button>
                      </div>
                      {formData.roles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.roles.map(role => (
                            <Badge key={role} className="bg-accent text-white pr-1">
                              {role}
                              <button
                                type="button"
                                onClick={() => removeRole(role)}
                                className="ml-2 hover:bg-white/20 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">Add multiple roles one by one</p>
                    </div>
                  </div>

                  {/* Location & Timeline */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-accent" />
                      Location & Timeline
                    </h3>
                    
                    {/* Multiple Locations */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Location(s) *</label>
                      <div className="flex gap-2">
                        <Input
                          value={currentLocation}
                          onChange={(e) => setCurrentLocation(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                          placeholder="e.g., Mumbai, Hyderabad, Dubai"
                        />
                        <Button type="button" onClick={addLocation} className="bg-accent hover:bg-accent/90">
                          Add
                        </Button>
                      </div>
                      {formData.locations.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.locations.map(location => (
                            <Badge key={location} className="bg-accent text-white pr-1">
                              {location}
                              <button
                                type="button"
                                onClick={() => removeLocation(location)}
                                className="ml-2 hover:bg-white/20 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">Add multiple cities or countries</p>
                    </div>

                    {/* Multiple Durations/Timelines */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Duration/Timeline</label>
                        <div className="flex gap-2">
                          <Input
                            value={currentDuration}
                            onChange={(e) => setCurrentDuration(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDuration())}
                            placeholder="e.g., Mumbai: 30 days"
                          />
                          <Button type="button" onClick={addDuration} className="bg-accent hover:bg-accent/90">
                            Add
                          </Button>
                        </div>
                        {formData.durations.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.durations.map(duration => (
                              <Badge key={duration} className="bg-orange-ultralight text-accent border-accent/30 pr-1">
                                {duration}
                                <button
                                  type="button"
                                  onClick={() => removeDuration(duration)}
                                  className="ml-2 hover:bg-accent/20 rounded-full p-0.5"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Add timeline for each location</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Start Date *</label>
                        <Input
                          required
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>

                    {/* Ad Expiry Feature */}
                    <div className="mt-4 p-4 bg-accent/5 rounded-lg border border-accent/20">
                      <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-accent" />
                        Ad Expiry Settings
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Quick Select</label>
                          <Select 
                            value={formData.expiryPreset} 
                            onValueChange={(val) => {
                              setFormData({ ...formData, expiryPreset: val });
                              if (val !== 'custom') {
                                const today = new Date();
                                const days = parseInt(val);
                                const expiryDate = new Date(today.setDate(today.getDate() + days));
                                setFormData({ 
                                  ...formData, 
                                  expiryPreset: val,
                                  expiryDate: expiryDate.toISOString().split('T')[0]
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">7 days</SelectItem>
                              <SelectItem value="15">15 days</SelectItem>
                              <SelectItem value="30">30 days (Recommended)</SelectItem>
                              <SelectItem value="60">60 days</SelectItem>
                              <SelectItem value="custom">Custom Date</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">
                            Run Ad Until * 
                            {formData.expiryDate && (
                              <span className="text-xs text-muted-foreground ml-2">
                                ({Math.ceil((new Date(formData.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))} days)
                              </span>
                            )}
                          </label>
                          <Input
                            required
                            type="date"
                            value={formData.expiryDate}
                            onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value, expiryPreset: 'custom' })}
                            min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        📌 Your hiring post will automatically be removed from active listings after the expiry date. You&apos;ll receive a notification 1 day before expiry with an option to extend.
                      </p>
                    </div>
                  </div>

                  {/* Project Description */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <FileText className="w-5 h-5 text-accent" />
                      Description & Requirements
                    </h3>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Project Description (Optional)</label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe your project, its theme, and what you're looking for..."
                        rows={4}
                      />
                      <p className="text-xs text-muted-foreground mt-2">You can skip this if you prefer</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Requirements (one per line)</label>
                      <Textarea
                        value={formData.requirements}
                        onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                        placeholder="10+ years experience&#10;Period film expertise&#10;Team management skills"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Budget (Optional) */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-accent" />
                      Budget (Optional)
                    </h3>
                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox 
                        id="showBudget"
                        checked={formData.showBudget}
                        onCheckedChange={(checked) => setFormData({ ...formData, showBudget: checked })}
                      />
                      <label htmlFor="showBudget" className="text-sm font-medium cursor-pointer">
                        I want to disclose the budget range
                      </label>
                    </div>
                    {formData.showBudget && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Minimum Budget (per day)</label>
                          <Input
                            type="number"
                            value={formData.budgetMin}
                            onChange={(e) => setFormData({ ...formData, budgetMin: e.target.value })}
                            placeholder="e.g., 15000"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Maximum Budget (per day)</label>
                          <Input
                            type="number"
                            value={formData.budgetMax}
                            onChange={(e) => setFormData({ ...formData, budgetMax: e.target.value })}
                            placeholder="e.g., 20000"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Contact Email *</label>
                        <Input
                          required
                          type="email"
                          value={formData.contactEmail}
                          onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                          placeholder="your.email@company.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Contact Phone</label>
                        <Input
                          value={formData.contactPhone}
                          onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white py-6 text-lg">
                      Create Hiring Post
                    </Button>
                    <p className="text-xs text-center text-muted-foreground mt-4">
                      Your hiring post will be reviewed and published within 24 hours
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PostProjectPage;